import { useVirtualizer } from '@tanstack/react-virtual';
import {
  Children,
  DetailedHTMLProps,
  HTMLAttributes,
  MutableRefObject,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { GroupBase, MenuListProps } from 'react-select';
import { DEFAULT_OPTION_HEIGHT, SELECT_OVERSCAN_COUNT } from './constants';
import { VirtualizedOptionListItem } from './Select.styles';
import { BaseSelectMenuList } from './SelectMenuList';
import { BaseOption } from './types';

export const VirtualizedSelectMenuList = (
  props: MenuListProps<BaseOption<string>, boolean, GroupBase<BaseOption<string>>>,
) => {
  const scrollElementRef = useRef<HTMLDivElement | null>(null);
  const rows = useMemo(() => Children.toArray(props.children), [props.children]);

  const virtualizer = useVirtualizer({
    count: rows.length,
    overscan: SELECT_OVERSCAN_COUNT,
    getScrollElement: useCallback(() => scrollElementRef.current, []),
    estimateSize: useCallback(() => DEFAULT_OPTION_HEIGHT, []),
    getItemKey: useCallback(
      (index: number) => (props?.options?.[index] as BaseOption<string>)?.value || index,
      [props.options],
    ),
  });

  return (
    <BaseSelectMenuList
      {...props}
      containerStyles={{ height: virtualizer.getTotalSize() }}
      innerRef={(node) => {
        scrollElementRef.current = node;
        if (typeof props.innerRef === 'function') {
          props.innerRef(node);
        } else if (props.innerRef) {
          (props.innerRef as MutableRefObject<HTMLDivElement | null>).current = node;
        }
      }}
      innerProps={
        { 'data-test': 'virtualized-select-menu-list', ...props.innerProps } as DetailedHTMLProps<
          HTMLAttributes<HTMLDivElement>,
          HTMLDivElement
        >
      }
    >
      {virtualizer.getVirtualItems().map(({ start, key, index }) => {
        const child = rows[index];
        return (
          <VirtualizedOptionListItem
            key={key}
            data-test="virtualized-select-option-list-item"
            data-index={index} // This is used by @tanstack/react-virtual to measure the element using the ref
            ref={virtualizer.measureElement}
            style={{
              transform: `translateY(${start}px)`,
            }}
          >
            {child}
          </VirtualizedOptionListItem>
        );
      })}
    </BaseSelectMenuList>
  );
};
